import { useEffect, useState } from "react";
import { ArrowUpRight, Bank, CheckCircle, CurrencyDollar, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";

export default function WalletPanel() {
  const [data, setData] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [payForm, setPayForm] = useState({ promoter_id: "", amount: "", method: "wallet" });
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try {
      const { data: wallet } = await API.get("/wallet");
      setData(wallet);
      setLoadError("");
    } catch (e) {
      setLoadError(fmtErr(e));
      const status = await API.get("/payments/connect/status").catch(() => ({ data: {} }));
      setData({ balance: 0, pending: 0, currency: "usd", ledger: [], earnings: [], connect: status.data });
    }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="py-10 text-sm text-zinc-500">Loading wallet...</div>;

  const connectPayouts = async () => {
    setBusy(true);
    try {
      const { data: res } = await API.post("/payments/connect/onboard", { origin_url: window.location.origin });
      window.location.href = res.onboarding_url;
    } catch (e) {
      setConnectError(fmtErr(e));
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data: res } = await API.post("/wallet/withdraw", { amount: Number(withdrawAmount) });
      toast.success(`$${res.amount.toFixed(2)} sent to your Stripe payout account`);
      setWithdrawAmount("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const payPromoter = async () => {
    setBusy(true);
    try {
      await API.post("/wallet/pay-promoter", {
        promoter_id: payForm.promoter_id, amount: Number(payForm.amount), method: payForm.method,
      });
      toast.success(payForm.method === "wallet" ? "Payout sent to promoter wallet" : "Cash payout recorded");
      setPayForm({ promoter_id: "", amount: "", method: "wallet" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const payoutsReady = data.connect?.connected && data.connect?.payouts_enabled;

  return (
    <div className="space-y-6" data-testid="wallet-panel">
      {loadError && (
        <div className="rounded-xl border border-neon-yellow/30 bg-neon-yellow/5 px-3 py-2 text-xs font-bold text-neon-yellow" data-testid="wallet-load-error">
          {loadError === "No wallet for this account" ? "Link your venue account before connecting payouts." : loadError}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-neon-gold/40 bg-[#141005] p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Wallet Balance</div>
          <div className="mt-2 font-display text-3xl font-black text-neon-gold" data-testid="wallet-balance">
            ${data.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
        {data.role === "promoter" && data.earnings && (
          <>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Owed (All Venues)</div>
              <div className="mt-2 font-display text-3xl font-black">${data.earnings.owed.toLocaleString()}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Received (Wallet)</div>
              <div className="mt-2 font-display text-3xl font-black text-neon-green">${data.earnings.paid_wallet.toLocaleString()}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Received (Cash)</div>
              <div className="mt-2 font-display text-3xl font-black text-neon-violet">${data.earnings.paid_cash.toLocaleString()}</div>
            </div>
          </>
        )}
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
        <div className="flex items-center gap-2 font-display text-sm font-black"><Bank size={18} className="text-neon-violet" /> Payout Account</div>
        {(connectError || data.connect?.platform_ready === false) && (
          <div className="mt-4 rounded-2xl border border-neon-yellow/40 bg-neon-yellow/10 p-4" data-testid="connect-platform-warning">
            <div className="text-xs font-black uppercase tracking-widest text-neon-yellow">Platform owner action required</div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-300">
              Stripe Connect is not activated on the NITEPASS platform account. The owner must open Stripe Dashboard → Connect, activate Connect, complete the platform profile, then return here and retry.
            </p>
            <a href="https://dashboard.stripe.com/connect" target="_blank" rel="noreferrer"
              className="mt-3 inline-flex rounded-full bg-neon-yellow px-4 py-2 text-[11px] font-black text-black" data-testid="stripe-connect-dashboard-link">
              Open Stripe Connect Dashboard
            </a>
            {connectError && <p className="mt-2 text-[11px] text-zinc-500">{connectError}</p>}
          </div>
        )}
        {payoutsReady ? (
          <div className="mt-3 flex items-center gap-2 text-sm font-bold text-neon-green" data-testid="payouts-connected">
            <CheckCircle size={17} weight="fill" /> Bank connected — withdrawals enabled
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <p className="text-xs text-zinc-400">
              {data.connect?.connected ? "Finish Stripe onboarding to enable withdrawals." : "Connect a bank account via Stripe to withdraw wallet funds."}
            </p>
            <button data-testid="connect-payouts-btn" onClick={connectPayouts} disabled={busy}
              className="rounded-full bg-neon-violet px-5 py-2 text-xs font-black text-black shadow-glow-violet disabled:opacity-50">
              {data.connect?.platform_ready === false ? "Retry After Owner Setup" : data.connect?.connected ? "Finish Setup" : "Connect Payouts"}
            </button>
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <input data-testid="withdraw-amount-input" type="number" min="1" step="0.01" value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="Amount $"
            className="w-40 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
          <button data-testid="withdraw-btn" onClick={withdraw} disabled={busy || !withdrawAmount || !payoutsReady}
            className="rounded-full bg-neon-gold px-5 py-2 text-xs font-black text-black shadow-glow-gold disabled:opacity-40">
            <ArrowUpRight size={14} className="mr-1 inline" />Withdraw to Bank
          </button>
        </div>
      </div>

      {data.role === "venue" && (
        <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
          <div className="flex items-center gap-2 font-display text-sm font-black"><HandCoins size={18} className="text-neon-gold" /> Promoter Payouts</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm" data-testid="outstanding-table">
              <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="py-2 pr-4">Promoter</th><th className="pr-4">Owed</th><th className="pr-4">Paid (Wallet)</th><th className="pr-4">Paid (Cash)</th><th>Remaining</th>
              </tr></thead>
              <tbody>
                {data.outstanding.map((o) => (
                  <tr key={o.promoter_id} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 font-bold">{o.handle}</td>
                    <td className="pr-4">${o.owed.toFixed(2)}</td>
                    <td className="pr-4 text-neon-green">${o.paid_wallet.toFixed(2)}</td>
                    <td className="pr-4 text-neon-violet">${o.paid_cash.toFixed(2)}</td>
                    <td className="font-bold text-neon-gold">${o.remaining.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.outstanding.length === 0 && <p className="py-4 text-sm text-zinc-500">No promoter earnings yet — they accrue from verified scans.</p>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select data-testid="pay-promoter-select" value={payForm.promoter_id}
              onChange={(e) => setPayForm({ ...payForm, promoter_id: e.target.value })}
              className="rounded-xl border border-white/10 bg-black px-3 py-2.5 text-sm outline-none">
              <option value="">Select promoter</option>
              {data.outstanding.filter((o) => o.remaining > 0).map((o) => (
                <option key={o.promoter_id} value={o.promoter_id}>{o.handle} — ${o.remaining.toFixed(2)} due</option>
              ))}
            </select>
            <input data-testid="pay-amount-input" type="number" min="1" step="0.01" placeholder="Amount $"
              value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
              className="w-32 rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm outline-none focus:border-neon-gold" />
            <div className="flex overflow-hidden rounded-full border border-white/10">
              {[["wallet", "From Wallet"], ["cash", "Cash"]].map(([m, label]) => (
                <button key={m} data-testid={`pay-method-${m}`} type="button"
                  onClick={() => setPayForm({ ...payForm, method: m })}
                  className={`px-4 py-2.5 text-xs font-bold transition-colors duration-150 ${payForm.method === m ? "bg-neon-gold text-black" : "text-zinc-400"}`}>
                  {label}
                </button>
              ))}
            </div>
            <button data-testid="pay-submit-btn" onClick={payPromoter} disabled={busy || !payForm.promoter_id || !payForm.amount}
              className="rounded-full bg-neon-gold px-5 py-2.5 text-xs font-black text-black shadow-glow-gold disabled:opacity-40">
              Send Payout
            </button>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">Wallet payouts move money instantly to the promoter's withdrawable balance. Cash records the settlement for the books without moving wallet funds.</p>
        </div>
      )}

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
        <div className="mb-3 flex items-center gap-2 font-display text-sm font-black"><CurrencyDollar size={17} className="text-neon-green" /> Ledger</div>
        <div className="space-y-2" data-testid="wallet-ledger">
          {data.ledger.map((l) => (
            <div key={l.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-2.5 text-xs">
              <div>
                <span className="font-bold">{l.note}</span>
                <span className="ml-2 rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500">{l.category.replace(/_/g, " ")}</span>
              </div>
              <div className="text-right">
                <span className={`font-black ${l.direction === "credit" ? "text-neon-green" : "text-zinc-400"}`}>
                  {l.direction === "credit" ? "+" : "−"}${l.amount.toFixed(2)}
                </span>
                <div className="text-[10px] text-zinc-600">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {data.ledger.length === 0 && <p className="py-4 text-sm text-zinc-500">No wallet activity yet.</p>}
        </div>
      </div>
    </div>
  );
}
